import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { ChatMessage } from '~/pages/home/models/chat-message.model';

export interface SearchSettings {
    numberOfDocuments: number;
    searchType: 'quick' | 'detailed';
    selectedAgent: 'auto' | 'general' | 'ces_search' | 'ewo_qna' | 'ewo_search' | 'legacy_piping' | 'esra' | 'rims';
    generateDrtTable?: boolean;
}

export interface ChatSessionState {
    threadId: string;
    isChatMode: boolean;
    chatMessages: ChatMessage[];
    chatHistory: [string, string][];
    promptText: string;
    selectedFiles: string[];
}

@Injectable({
    providedIn: 'root'
})
export class TabNavigationService {
    private readonly chatSessionStorageKey = 'intel-engine-chat-session-v1';
    // TODO [BACKEND-HISTORY-INTEGRATION]:
    // This storage-backed chat session is a frontend fallback for route/reload continuity
    // before backend thread-history APIs are integrated.
    // Revisit after get-thread-history is available; keep only draft state if preferred.

    // Emit when user requests a fresh chat
    private newChatSubject = new Subject<void>();
    public newChat$ = this.newChatSubject.asObservable();

    private searchSettingsSubject = new BehaviorSubject<SearchSettings>({
        numberOfDocuments: 6,
        searchType: 'quick',
        selectedAgent: 'auto',
        generateDrtTable: false
    });
    public searchSettings$ = this.searchSettingsSubject.asObservable();

    private chatSessionSubject = new BehaviorSubject<ChatSessionState>({
        threadId: '',
        isChatMode: false,
        chatMessages: [],
        chatHistory: [],
        promptText: '',
        selectedFiles: []
    });
    public chatSession$ = this.chatSessionSubject.asObservable();

    constructor() {
        this.hydrateChatSessionFromStorage();
    }

    /**
     * Trigger a fresh chat session across the app
     */
    public triggerNewChat(): void {
        this.newChatSubject.next();
    }

    public updateSearchSettings(settings: Partial<SearchSettings>): void {
        const current = this.searchSettingsSubject.value;
        this.searchSettingsSubject.next({
            ...current,
            ...settings
        });
    }

    public getSearchSettings(): SearchSettings {
        return this.searchSettingsSubject.value;
    }

    public updateChatSession(session: Partial<ChatSessionState>): void {
        const current = this.chatSessionSubject.value;
        const nextSession = {
            ...current,
            ...session
        };

        this.chatSessionSubject.next(nextSession);
        this.persistChatSessionToStorage(nextSession);
    }

    public getChatSession(): ChatSessionState {
        return this.chatSessionSubject.value;
    }

    public resetChatSession(): void {
        const emptySession = {
            threadId: '',
            isChatMode: false,
            chatMessages: [],
            chatHistory: [],
            promptText: '',
            selectedFiles: []
        };

        this.chatSessionSubject.next(emptySession);

        try {
            sessionStorage.removeItem(this.chatSessionStorageKey);
        } catch {
        }
    }

    private hydrateChatSessionFromStorage(): void {
        // TODO [BACKEND-HISTORY-INTEGRATION]:
        // Hydration is currently required because chat messages are not fetched from backend on reload.
        // After backend history is wired in HomeComponent, this can become optional/fallback only.
        try {
            const serialized = sessionStorage.getItem(this.chatSessionStorageKey);

            if (!serialized) {
                return;
            }

            const parsed = JSON.parse(serialized) as ChatSessionState;

            if (!parsed || typeof parsed !== 'object') {
                return;
            }

            const hydratedSession: ChatSessionState = {
                threadId: parsed.threadId || '',
                isChatMode: !!parsed.isChatMode,
                chatMessages: Array.isArray(parsed.chatMessages) ? parsed.chatMessages : [],
                chatHistory: Array.isArray(parsed.chatHistory) ? parsed.chatHistory : [],
                promptText: parsed.promptText || '',
                selectedFiles: Array.isArray(parsed.selectedFiles) ? parsed.selectedFiles : []
            };

            this.chatSessionSubject.next(hydratedSession);
        } catch {
            this.chatSessionSubject.next({
                threadId: '',
                isChatMode: false,
                chatMessages: [],
                chatHistory: [],
                promptText: '',
                selectedFiles: []
            });
        }
    }

    private persistChatSessionToStorage(session: ChatSessionState): void {
        try {
            sessionStorage.setItem(this.chatSessionStorageKey, JSON.stringify(session));
        } catch {
        }
    }
}
